import useDebounce from 'hooks/useDebounce';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Badge, Button, Card, Stack } from 'react-bootstrap';
import {
  enrichSchedules,
  generateEventsBetween,
  getScheduleEventProps,
  getScheduleLabel,
} from 'utils/recurrenceUtils';
import { useSchedules } from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import ScheduleCalendar from 'features/Responder/features/Schedules/features/ScheduleBuilder/components/RruleBuilder/components/ScheduleCalendar';

export default function ScheduleSelectorPanel({
  onCreateSchedule = null,
  onEditSelectedSchedule = null,
  onDeleteSelectedSchedule = null,
  onRefreshRequest = null,
}) {
  const scheduleTabs = useMemo(() => [{ key: 'calendar', label: 'Calendar' }], []);
  const [activeTab, setActiveTab] = useState('calendar');

  const { colorMap, schedules, fetchSchedules, activeEditSchedule, setActiveEditSchedule } =
    useSchedules();
  const showCrudActions =
    typeof onCreateSchedule === 'function' ||
    typeof onEditSelectedSchedule === 'function' ||
    typeof onDeleteSelectedSchedule === 'function';

  const [startDate, setStartDate] = useState(() => new Date(new Date().getFullYear(), 0, 1));
  const [endDate, setEndDate] = useState(() => new Date(new Date().getFullYear() + 1, 0, 1));

  const [enrichedSchedules, setEnrichedSchedules] = useState([]);

  // Enrich schedules with labels and colors whenever schedules change
  // prevents recalculation on every render
  useEffect(() => {
    setEnrichedSchedules(enrichSchedules(schedules || []));
  }, [schedules]);

  const [filterString, setFilterString] = useState('');
  const debouncedFilterString = useDebounce(filterString, 500);

  const [allEvents, setAllEvents] = useState([]);
  const [isHydratingEvents, setIsHydratingEvents] = useState(false);

  useEffect(() => {
    const schedulesToBuild = Array.isArray(enrichedSchedules) ? enrichedSchedules : [];
    if (!schedulesToBuild.length) {
      setAllEvents([]);
      setIsHydratingEvents(false);
      return undefined;
    }

    let isActive = true;
    let timeoutId = null;
    const nextEvents = [];
    const chunkSize = 12;
    let index = 0;

    setIsHydratingEvents(true);

    const buildChunk = () => {
      if (!isActive) return;
      const endIndex = Math.min(index + chunkSize, schedulesToBuild.length);

      for (; index < endIndex; index += 1) {
        const schedule = schedulesToBuild[index];
        let opacity = 1.0;
        if (
          debouncedFilterString &&
          !schedule.name.toLowerCase().includes(debouncedFilterString.toLowerCase())
        ) {
          opacity = 0.3;
        }
        const occurrences = generateEventsBetween(schedule, startDate, endDate);
        occurrences.forEach((occurrence) => {
          nextEvents.push({
            ...occurrence,
            opacity,
          });
        });
      }

      if (index < schedulesToBuild.length) {
        timeoutId = window.setTimeout(buildChunk, 0);
        return;
      }

      setAllEvents(nextEvents);
      setIsHydratingEvents(false);
    };

    timeoutId = window.setTimeout(buildChunk, 0);

    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [debouncedFilterString, endDate, enrichedSchedules, startDate]);

  // show only events from today
  const [useFilter, setUseFilter] = useState(true);
  const handleViewChange = useCallback(
    (view, range) => {
      if (view === 'agenda') {
        setUseFilter(true);
      } else {
        setUseFilter(false);
      }
    },
    [startDate, endDate],
  );

  useEffect(() => {
    if (debouncedFilterString) {
      console.log('Setting filter string to:', debouncedFilterString);
      setFilterString(debouncedFilterString);
    }
  }, [debouncedFilterString]);

  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const onRangeChange = (range) => {
    let year;
    if (Array.isArray(range) && range.length > 0) {
      // week view
      year = new Date(range[0]).getFullYear();
    } else if (range.start && range.end) {
      // month view
      year = new Date(range.start).getFullYear();
    }
    if (year && year !== currentYear) {
      setCurrentYear(year);
      setStartDate(new Date(year - 1, 11, 26)); // Dec 26 of previous year
      setEndDate(new Date(year + 1, 0, 6)); // Jan 6 of next year
      console.log('Range changed to', range, 'using year', year);
    }
  };

  const selectionFilter = (event) => {
    return true;
    if (!useFilter) return true;
    const today = new Date();
    return (
      event.start >= new Date(today.getFullYear(), today.getMonth(), today.getDate()) &&
      event.start < new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
    );
  };

  const findScheduleByName = (name, schedules) => {
    return schedules.find((s) => name.includes(getScheduleLabel(s)));
  };

  return (
    <Card className="shadow-sm h-100 bg-dark text-white border-secondary">
      <Card.Body className="d-flex flex-column h-100">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <Card.Title className="mb-0">Schedule Calendar</Card.Title>
            <Card.Text className="text-muted small mb-0">
              Select a schedule to view telemetry.
            </Card.Text>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              className="responder-interactive-action"
              onClick={() => {
                if (typeof onRefreshRequest === 'function') {
                  onRefreshRequest();
                  return;
                }
                fetchSchedules?.();
              }}
            >
              Refresh
            </Button>
            {showCrudActions && (
              <>
                {typeof onCreateSchedule === 'function' && (
                  <Button
                    size="sm"
                    variant="primary"
                    className="responder-interactive-action"
                    onClick={onCreateSchedule}
                  >
                    Create
                  </Button>
                )}
                {typeof onEditSelectedSchedule === 'function' && (
                  <Button
                    size="sm"
                    variant="outline-light"
                    className="responder-interactive-action"
                    disabled={!activeEditSchedule}
                    onClick={onEditSelectedSchedule}
                  >
                    Edit
                  </Button>
                )}
                {typeof onDeleteSelectedSchedule === 'function' && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="responder-interactive-action"
                    disabled={!activeEditSchedule}
                    onClick={onDeleteSelectedSchedule}
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* <div className="d-flex gap-2 flex-wrap mb-2">
          {scheduleTabs.map((tab) => (
            <Button
              key={tab.key}
              size="sm"
              variant={activeTab === tab.key ? 'primary' : 'outline-light'}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </Button>
          ))}
        </div> */}
        {/* <div className="d-flex gap-2 flex-wrap mb-2">
          <Button
            key="filter"
            size="sm"
            variant={useFilter ? 'primary' : 'outline-light'}
            onClick={() => setUseFilter(!useFilter)}
          >
            Filter
          </Button>
        </div> */}
        {activeTab === 'calendar' && (
          <div className="flex-grow-1" style={{ minHeight: 0 }}>
            <ScheduleCalendar
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              events={allEvents.filter(selectionFilter).filter((event) => {
                return true;
                //if using HIDE mode, remove above return
                if (!debouncedFilterString) return true;
                return event.title.toLowerCase().includes(debouncedFilterString.toLowerCase());
              })}
              setFilterString={setFilterString}
              filterString={filterString}
              debouncedFilterString={debouncedFilterString}
              showAgenda
              showDaily
              onView={handleViewChange}
              onRangeChange={onRangeChange}
              isHydratingEvents={isHydratingEvents}
              onSelectEvent={(event) => {
                // Find schedule whose label is included in event title and start time matches
                const selectedSchedule = findScheduleByName(event.title, enrichedSchedules);
                if (selectedSchedule) {
                  setActiveEditSchedule(selectedSchedule);
                }
              }}
              eventPropGetter={(event) => {
                const schedule = findScheduleByName(event.title, enrichedSchedules);
                let props = getScheduleEventProps(event, schedule);
                return props;
              }}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
